// Port of src/app/components/nav-bar/dropdowns/filters/filter-dropdown/
// filter-dropdown.component.{ts,html,scss} (mypart-filter-dropdown).
//
// CDK Overlay replacement: the dropdown renders into document.body via
// createPortal. The NavBar (which owned the OverlayRef) passes the host
// position style (the Angular setPositionOffScreen/Left/Right/Center calls
// mutated the :host element's top/left/right — here the same values arrive as
// a style object). hasBackdrop:true → an .overlay-backdrop div behind the
// panel whose click closes (Angular backdropClick subscription); the panel's
// (outsideClick) is replicated with useOutsideClick.
//
// ngOnInit's sync of subFilter states from selectedFilters mutates the filter
// tree in place exactly like Angular; consumers re-render via filtersVersion.

import { useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import type { Filter, SubFilter } from '../../interfaces/filter';
import { useFiltersStore } from '../../stores/filters-store';
import { useGlobalStore } from '../../stores/global-store';
import { useIsEllipsis } from '../../shared/hooks/useIsEllipsis';
import { useOutsideClick } from '../../shared/hooks/useOutsideClick';
import { debounce } from '../../shared/utils/debounce';
import './FilterDropdown.scss';

export interface FilterDropdownProps {
  filter: Filter;
  isInputFilter?: boolean;
  /** Host position (Angular Renderer2 top/left/right writes on :host). */
  position: CSSProperties;
  onFiltersClicked: () => void;
  onOutsideDropdownClick: () => void;
}

function SubFilterItem({
  subFilter,
  onTextClicked,
  onToggle
}: {
  subFilter: SubFilter;
  onTextClicked: (subFilter: SubFilter) => void;
  onToggle: (subFilter: SubFilter, state: 'green' | 'red') => void;
}) {
  const { ref } = useIsEllipsis<HTMLSpanElement>();

  return (
    <li className="mt sm:my-0">
      <div className="flex items-center rounded hover:bg-gray-200 dark:hover:bg-gray-600 filter-container">
        <span ref={ref}
              onClick={() => onTextClicked(subFilter)}
              className={`dark:border-gray-600 cursor-pointer w-full py-2 mx-2 text-sm font-medium text-gray-800 rounded
                     max-[600px]:whitespace-normal dark:text-gray-300 ellipsis${subFilter.state === 'red' ? ' !text-red-600' : ''}${subFilter.state === 'green' ? ' !text-green-500' : ''}`}>
          {subFilter.name}
        </span>

        <div className="sm:hidden flex items-center justify-center mx-2 filter-actions">
          <svg onClick={() => onToggle(subFilter, 'red')}
               aria-hidden="true"
               className={`cursor-pointer mr-6 sm:mr-4 w-6 h-5 sm:w-4 sm:h-3 text-gray-800 dark:text-gray-300
                      hover:text-black hover:dark:text-gray-50 active:text-red-600${subFilter.state === 'red' ? ' !text-red-600' : ''}`}
               fill="none"
               viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">
            <path d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" stroke="currentColor" strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2" />
          </svg>

          <svg onClick={() => onToggle(subFilter, 'green')}
               aria-hidden="true"
               className={`cursor-pointer w-6 h-6 sm:w-4 sm:h-4 text-gray-800 dark:text-gray-300 hover:text-black
                      hover:dark:text-gray-50 active:text-green-500${subFilter.state === 'green' ? ' !text-green-500' : ''}`}
               fill="none"
               viewBox="0 0 16 12" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 5.917 5.724 10.5 15 1.5" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round"
                  strokeWidth="2" />
          </svg>
        </div>
      </div>
    </li>
  );
}

export default function FilterDropdown({
  filter,
  isInputFilter = false,
  position,
  onFiltersClicked,
  onOutsideDropdownClick
}: FilterDropdownProps) {
  // Re-render on in-place filter-tree mutations.
  useFiltersStore(s => s.filtersVersion);
  const lyricsContains = useFiltersStore(s => s.lyricsContains);
  const showBigSearchHomePage = useGlobalStore(s => s.showBigSearchHomePage);

  // ngOnInit: sync subFilter states from selectedFilters (in-place, like Angular).
  useMemo(() => {
    const selectedFilters = useFiltersStore.getState().selectedFilters;
    if (Object.prototype.hasOwnProperty.call(selectedFilters, filter.title)) {
      const selectedFilter = selectedFilters[filter.title];
      filter.filters.forEach(subFilter => {
        const selectedSubFilter = selectedFilter.filters.find(sf => sf.id === subFilter.id);
        if (selectedSubFilter) {
          subFilter.state = selectedSubFilter.state;
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const [searchTerm, setSearchTerm] = useState('');
  const [filteredSubFilters, setFilteredSubFilters] = useState<SubFilter[]>(filter.filters);

  // searchTerm.valueChanges.pipe(debounceTime(100)) → filterSubFilters().
  const debouncedFilterSubFilters = useMemo(
    () =>
      debounce((value: string) => {
        if (!value) {
          setFilteredSubFilters(filter.filters);
        } else {
          setFilteredSubFilters(filter.filters.filter(subFilter =>
            subFilter.name.toLowerCase().includes(value.toLowerCase())
          ));
        }
      }, 100),
    [filter]
  );

  // ngAfterViewInit: showSearchWordInThemeFilter.
  const showSearchWordInThemeFilter = filter.title === 'Lyrical Theme';

  const panelRef = useOutsideClick<HTMLDivElement>(() => onOutsideDropdownClick());

  const emitFiltersClicked = () => {
    // Use a microtask
    Promise.resolve().then(() => {
      onFiltersClicked();
    });
  };

  const addLyricsContains = () => {
    useFiltersStore.getState().setLyricsContains(searchTerm);
    setSearchTerm('');
    debouncedFilterSubFilters('');
    emitFiltersClicked();
  };

  const removeLyricsContains = () => {
    useFiltersStore.getState().setLyricsContains(undefined);
    emitFiltersClicked();
  };

  const removeFilter = (subFilter: SubFilter) => {
    useFiltersStore.getState().removeFilter(filter, subFilter);
  };

  const toggleFilter = (subFilter: SubFilter, state: 'green' | 'red') => {
    if (subFilter.state === state) {
      removeFilter(subFilter);
    } else {
      useFiltersStore.getState().addOrChangeSubFilterSelectedFilters(filter, subFilter, state, isInputFilter);
    }
    emitFiltersClicked();
  };

  const filterTextClicked = (subFilter: SubFilter) => {
    if (subFilter.state === 'none') {
      toggleFilter(subFilter, 'green');
    } else {
      toggleFilter(subFilter, subFilter.state);
    }
  };

  const { ref: lyricsEllipsisRef } = useIsEllipsis<HTMLSpanElement>();

  return createPortal(
    <>
      <div className="overlay-backdrop" style={{ zIndex: 49 }} onClick={onOutsideDropdownClick} />
      <div className="filter-dropdown-host" style={position}>
        <div ref={panelRef}
             className={`w-[300px] max-h-[300px] overflow-y-auto mt-2 z-30 py-1 bg-white dark:bg-zinc-800 shadow-xl rounded-md${showBigSearchHomePage ? ' !max-h-[260px]' : ''}`}>
          <form className="p-3" onSubmit={(e) => e.preventDefault()}>
            <label className="sr-only" htmlFor="input-group-search">Search {filter.title.toLocaleLowerCase()}</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <svg aria-hidden="true" className="w-4 h-4 text-gray-500 dark:text-gray-400"
                     fill="none" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z" stroke="currentColor"
                        strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
                </svg>
              </div>
              <input value={searchTerm}
                     onChange={(e) => {
                       setSearchTerm(e.target.value);
                       debouncedFilterSubFilters(e.target.value);
                     }}
                     className="block shadow:md w-full p-2 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50
                         focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-600 dark:border-gray-500
                         dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                     id="input-group-search" placeholder={`Search ${filter.title.toLocaleLowerCase()}`} type="text" />
              {showSearchWordInThemeFilter && searchTerm !== '' && filteredSubFilters.length === 0 && (
                <button onClick={(e) => {
                          e.stopPropagation();
                          addLyricsContains();
                        }}
                        className="absolute right-2 bottom-[.25rem] text-gray-900 bg-gray-100 border border-gray-300 focus:outline-none
              hover:bg-gray-200 focus:ring-0 font-medium rounded-full text-xs px-2 py-1.5 dark:bg-gray-800 dark:text-white
          dark:border-gray-600 dark:hover:bg-gray-700 dark:hover:border-gray-600"
                        type="button">
                  Contains
                </button>
              )}
            </div>
          </form>
          <ul className="px-3 pb-3 text-sm text-gray-700 dark:text-gray-200">
            {showSearchWordInThemeFilter && lyricsContains && (
              <li>
                <div className="relative flex items-center pl-2 rounded hover:bg-gray-200 dark:hover:bg-gray-600">
                  <span ref={lyricsEllipsisRef} className="text-green-500 w-full py-2 ml-2 text-sm font-medium rounded
                    max-[600px]:whitespace-normal ellipsis">
                    {lyricsContains}
                  </span>
                  <button onClick={(e) => {
                            e.stopPropagation();
                            removeLyricsContains();
                          }}
                          className="absolute inset-y-0 right-0 flex items-center pr-3"
                          type="button">
                    <svg aria-hidden="true"
                         className="w-3 h-3 text-gray-500 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-600"
                         fill="none" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">
                      <path d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" stroke="currentColor" strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2" />
                    </svg>
                  </button>
                </div>
              </li>
            )}
            {filteredSubFilters.map((subFilter) => (
              <SubFilterItem key={subFilter.id}
                             subFilter={subFilter}
                             onTextClicked={filterTextClicked}
                             onToggle={toggleFilter} />
            ))}
          </ul>
        </div>
      </div>
    </>,
    document.body
  );
}
